import * as React from 'react';
import {
    createPopper,
    Options as PopperOptions,
    Instance as PopperInstance,
    State as PopperState,
    Modifier as PopperModifier,
} from '@popperjs/core';
import { mergeRefs } from '../utils/merge-refs';

type State = {
    styles: Record<string, Partial<CSSStyleDeclaration>>;
    attributes: Record<string, Record<string, string | boolean>>;
};

export interface UsePositionerProps extends Partial<PopperOptions> {
    matchWidth?: boolean;
}

const EMPTY_MODIFIERS: Array<PopperModifier<string, unknown>> = [];

export const usePositioner = (options: UsePositionerProps = {}) => {
    const {
        onFirstUpdate,
        placement = 'bottom',
        strategy = 'absolute',
        matchWidth = false,
        modifiers = EMPTY_MODIFIERS,
    } = options;

    const [referenceElement, setReferenceElement] = React.useState(null);
    const [popperElement, setPopperElement] = React.useState(null);
    const [arrowElement, setArrowElement] = React.useState(null);
    const [state, setState] = React.useState<State>({
        styles: {
            popper: {
                position: strategy,
                left: '0',
                top: '0',
            },
            arrow: {
                position: 'absolute',
            },
        },
        attributes: {},
    });

    const updateStateModifier = React.useMemo(
        () => ({
            name: 'updateState',
            enabled: true,
            phase: 'write',
            fn: ({ state }: { state: PopperState }) => {
                const elements = Object.keys(state.elements);

                setState({
                    styles: Object.fromEntries(elements.map((element) => [element, state.styles[element] || {}])),
                    attributes: Object.fromEntries(
                        elements.map((element) => [element, state.attributes[element] || {}]),
                    ),
                });
            },
            requires: ['computeStyles'],
        }),
        [],
    );

    const popperOptions = React.useMemo<PopperOptions>(
        () =>
            ({
                onFirstUpdate,
                placement,
                strategy,
                modifiers: [
                    ...modifiers,
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                        phase: 'main',
                    },
                    {
                        name: 'preventOverflow',
                        enabled: true,
                        phase: 'main',
                    },
                    {
                        name: 'flip',
                        enabled: true,
                        options: {
                            padding: 8,
                        },
                        phase: 'main',
                    },
                    {
                        name: 'arrow',
                        options: {
                            element: arrowElement,
                        },
                    },
                    {
                        name: 'matchWidth',
                        enabled: matchWidth,
                        phase: 'beforeWrite',
                        requires: ['computeStyles'],
                        fn: ({ state }) => {
                            state.styles.popper.width = `${state.rects.reference.width}px`;
                        },
                        effect:
                            ({ state }) =>
                            () => {
                                const reference = state.elements.reference as HTMLElement;
                                state.elements.popper.style.width = `${reference.offsetWidth}px`;
                            },
                    },
                    updateStateModifier,
                    { name: 'applyStyles', enabled: false },
                ],
            } as PopperOptions),
        [onFirstUpdate, placement, strategy, modifiers, updateStateModifier, matchWidth, arrowElement],
    );

    const popperInstanceRef = React.useRef<PopperInstance>();

    React.useLayoutEffect(() => {
        if (popperInstanceRef.current != null) {
            popperInstanceRef.current.setOptions(popperOptions);
        }
    }, [popperOptions]);

    React.useLayoutEffect(() => {
        if (referenceElement == null || popperElement == null) {
            return;
        }

        const popperInstance = createPopper(referenceElement, popperElement, popperOptions);
        popperInstanceRef.current = popperInstance;

        return () => {
            popperInstance.destroy();
            popperInstanceRef.current = null;
        };
    }, [referenceElement, popperElement, popperOptions]);

    const getReferenceProps = React.useCallback((props = {}) => {
        return {
            ...props,
            ref: mergeRefs(props.ref, setReferenceElement),
        };
    }, []);

    const getPopperProps = React.useCallback(
        (props = {}) => {
            return {
                ...props,
                ...state.attributes.popper,
                style: {
                    ...props.style,
                    ...state.styles.popper,
                },
                ref: mergeRefs(props.ref, setPopperElement),
            };
        },
        [state.attributes, state.styles],
    );

    const getArrowProps = React.useCallback(
        (props = {}) => {
            return {
                ...props,
                ...state.attributes.arrow,
                style: {
                    ...props.style,
                    ...state.styles.arrow,
                },
                ref: mergeRefs(props.ref, setArrowElement),
            };
        },
        [state.attributes, state.styles],
    );

    return {
        getReferenceProps,
        getPopperProps,
        getArrowProps,
        state: popperInstanceRef.current ? popperInstanceRef.current.state : null,
        update: popperInstanceRef.current ? popperInstanceRef.current.update : null,
        forceUpdate: popperInstanceRef.current ? popperInstanceRef.current.forceUpdate : null,
    } as const;
};
